// frontend/src/utils/forecastBatches.js
//
// The SaaS forecast grid's changed cells → the `{updates, creates}` payload
// `/api/forecasts/bulk-upsert/` expects.
//
// ── THE CELL HOLDS A FINAL, SO THE WIRE CARRIES A FINAL ─────────────────────
// The grid's month cell displays `FinalForecastUnits` — consensus plus the
// promotion's pinned contribution — so the number a merchant types over it is
// a final, and it is sent as one. This module never subtracts the promotion
// itself: the server resolves `consensus = final - promotion` inside the
// write's transaction (backend/forecasting/consensus.py), so the component
// cannot be stale, and hands the stored values back.
//
// ── UPDATE vs CREATE ────────────────────────────────────────────────────────
// A horizon month the engine has not written yet has no row to update, so an
// override for it is an INSERT. `ForecastID` present → update; absent → create.
//
// ── A CLEARED CELL ON A MONTH WITH NO ROW IS NOT AN OVERRIDE ────────────────
// `null` means "drop my override". On a month that has no forecast row there
// is no override to drop, so there is nothing to send. The server refuses such
// a create (`new_month_columns`); this module keeps the edit from being
// counted as dirty in the first place.
//
// ── WHAT IS NOT SENT ────────────────────────────────────────────────────────
// `ConsensusUserModified` is the server's to set — `write_final_units` stamps
// it on every write carrying units and clears it on a null.
//
// `ConsensusForecastUnits` is not sent either. It is a different number from
// what the cell shows whenever a promotion covers the month — see the wire
// note above.
//
// `ForecastCreationDate` is engine-owned; `bulk_upsert` stamps
// `store.get_local_time().date()` itself.

import { monthToForecastDate } from './forecastHorizon';

/**
 * @param {Iterable<{rowId: string|number, fieldName: string, newValue: number|null,
 *   rowData: object}>} changedCells  the values of `useEditableGridState`'s
 *   changed-cell map. `fieldName` is a `'YYYY-MM'` month key; `rowData` carries
 *   `ItemId` / `LocationId` and the per-month `<month>_forecast` row.
 * @returns {{updates: Array, creates: Array, pendingNodes: Array, skipped: number}}
 *   `pendingNodes` is parallel to `updates.concat(creates)` — every update in
 *   request order, then every create — NOT to the iteration order of the cells,
 *   which interleaves the two. That is what lets the caller read each cell's
 *   LANDED values out of the response by position: the endpoint answers in
 *   request order and a create's `id` is a ForecastID the caller has never seen,
 *   so position is the only correlation available.
 *   `skipped` counts cells with nothing to send — see the module header. It is
 *   a tripwire rather than a live count: the grid drops such a cell when it is
 *   edited, so a non-zero value here means that guard was bypassed.
 */
export function buildForecastBatches(changedCells) {
  const updates = [];
  const creates = [];
  const updateNodes = [];
  const createNodes = [];
  let skipped = 0;

  for (const { rowId, fieldName: monthKey, newValue, rowData } of changedCells) {
    const forecastId = rowData[`${monthKey}_forecast`]?.ForecastID;

    if (forecastId) {
      updates.push({ id: forecastId, FinalForecastUnits: newValue });
      updateNodes.push({ rowId, monthKey, newValue });
    } else if (newValue == null) {
      skipped += 1;
    } else {
      creates.push({
        ItemID_id: rowData.ItemId,
        LocationID_id: rowData.LocationId,
        ForecastDate: monthToForecastDate(monthKey),
        FinalForecastUnits: newValue,
      });
      createNodes.push({ rowId, monthKey, newValue });
    }
  }

  return {
    updates,
    creates,
    pendingNodes: [...updateNodes, ...createNodes],
    skipped,
  };
}

/**
 * Is editing `monthKey` on `rowData` to `value` a change the server would see?
 *
 * Only one case says no, and it is the one the header describes: clearing a
 * month that has no forecast row leaves the cell exactly as the server holds
 * it. The grid uses this to DROP the pending cell rather than record it, so
 * "Save (1)" never counts an edit that has nothing to save.
 *
 * @param {object} rowData
 * @param {string} monthKey  `'YYYY-MM'`
 * @param {number|null} value
 * @returns {boolean}
 */
export function isForecastCellChange(rowData, monthKey, value) {
  if (value != null) return true;
  return !!rowData?.[`${monthKey}_forecast`]?.ForecastID;
}
